/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { format, isThisYear } from 'date-fns';

import { ExecutionStatusBadge } from './ExecutionStatusBadge';

import { RunExecutionDetails } from '@/commons/protocols';
import { cn, displayUser } from '@/utils';
import { getExecutionSummaryData } from '@/utils/executions';

interface ExecutionLineProps {
    execution: RunExecutionDetails;
    onSelect: (id: string) => void;
    isSelected: boolean;
    showFullDate?: boolean;
    index?: number;
}

export const ExecutionLine = ({
    execution,
    onSelect,
    isSelected,
    showFullDate = false,
    index = 0,
}: ExecutionLineProps) => {
    const summary = getExecutionSummaryData(execution);
    const { pass, fail, other } = summary;

    const date = new Date(execution.createdAt);
    const dateFormat = showFullDate
        ? 'MMM dd, yyyy HH:mm'
        : isThisYear(date)
          ? "MM/dd 'at' HH:mm"
          : "MM/dd/yy 'at' HH:mm";

    return (
        <button
            onClick={() => onSelect(execution.id.toString())}
            className={cn(
                'flex items-center gap-[16px] px-[8px] py-[2px] h-[32px] hover:bg-slate-50 transition-colors w-full text-left outline-0',
                isSelected && 'bg-slate-50',
                index % 2 === 0 && 'bg-slate-50',
            )}
            aria-selected={isSelected}
            role="option"
            tabIndex={0}
        >
            <span className="text-body text-slate-700 w-[14%]">{format(date, dateFormat)}</span>
            <span className="text-detail text-gray-80">{displayUser(execution.triggerUser, true)}</span>
            <div className="ml-auto flex flex-wrap gap-2">
                <ExecutionStatusBadge status="PASS" number={pass} compact />
                <ExecutionStatusBadge status="FAIL" number={fail} compact />
                <ExecutionStatusBadge status="OTHER" number={other} compact />
            </div>
        </button>
    );
};
